// @archigraph eHS4iX7G
// Forgot password page for archigraph.io

import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ErrorMessage } from '../components/ui/ErrorMessage';
import { cognitoForgotPassword, cognitoConfirmForgotPassword } from '../services/cognito';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleRequestCode = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !EMAIL_REGEX.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    try {
      await cognitoForgotPassword(email);
      setStep('code');
      setSuccess('Check your email for a verification code.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!code.trim()) {
      setError('Verification code is required.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await cognitoConfirmForgotPassword(email, code, newPassword);
      navigate('/login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12">
      <Container className="max-w-[400px]">
        <p className="text-xs uppercase tracking-widest text-primary mb-4 font-bold">
          // reset password
        </p>

        {step === 'email' ? (
          <>
            <h1 className="text-2xl font-bold text-text mb-1">Forgot your password?</h1>
            <p className="text-xs text-text-lighter mb-8">
              Enter your email and we'll send you a verification code.
            </p>

            {error && <ErrorMessage message={error} onDismiss={() => setError('')} className="mb-6" />}

            <form onSubmit={handleRequestCode} noValidate className="space-y-5">
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                disabled={isLoading}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                disabled={isLoading}
                className="w-full"
              >
                Send Reset Code
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-text mb-1">Enter new password</h1>
            <p className="text-xs text-text-lighter mb-8">
              We sent a code to {email}
            </p>

            {success && (
              <div className="text-xs text-primary bg-primary/10 border border-primary/20 p-3 mb-6">
                {success}
              </div>
            )}
            {error && <ErrorMessage message={error} onDismiss={() => setError('')} className="mb-6" />}

            <form onSubmit={handleResetPassword} noValidate className="space-y-5">
              <Input
                label="Verification code"
                type="text"
                autoComplete="one-time-code"
                placeholder="Enter code from email"
                value={code}
                onChange={(e) => { setCode(e.target.value); setError(''); }}
                disabled={isLoading}
              />

              <Input
                label="New password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                disabled={isLoading}
              />

              <Input
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                disabled={isLoading}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                disabled={isLoading}
                className="w-full"
              >
                Reset Password
              </Button>
            </form>

            <button
              onClick={() => { setStep('email'); setError(''); setSuccess(''); }}
              className="text-xs text-text-lighter hover:text-text-light mt-4"
            >
              Use a different email
            </button>
          </>
        )}

        <div className="mt-8 pt-6 border-t border-border">
          <p className="text-xs text-text-lighter">
            Remember your password?{' '}
            <Link to="/login" className="text-primary hover:text-primary-light font-medium">
              Log in
            </Link>
          </p>
        </div>
      </Container>
    </div>
  );
}
